import pythonApiClient from './pythonBaseApi';

/**
 * Servicio para gestión de asignaciones de dieta a comensales
 * Maneja todas las operaciones CRUD relacionadas con comensales_dieta
 * 
 * NOTA: Los endpoints de dietas ahora usan Python (FastAPI)
 */
export const comensalDietaApi = {
  /**
   * Obtiene todas las asignaciones de dieta con filtros opcionales
   * @param {Object} filters - Filtros opcionales
   * @param {number} filters.id_comensal - ID del comensal
   * @param {number} filters.id_tipo_dieta - ID del tipo de dieta
   * @param {boolean} filters.activo - Estado activo
   * @param {string} filters.fecha_vigente - Fecha para verificar vigencia (YYYY-MM-DD)
   * @returns {Promise} - Respuesta de la API
   * 
   * NOTA: El backend de Python no tiene endpoint GET /comensales-dieta con filtros.
   * Usar getDietasByComensal para obtener dietas de un comensal específico.
   */
  getAll: (filters = {}) => {
    // Si hay filtro por comensal, usar el endpoint específico
    if (filters.id_comensal) {
      return pythonApiClient.get(`/mantenimientos/comensales/${filters.id_comensal}/dietas`);
    }
    
    // Si no hay filtro por comensal, retornar error ya que no hay endpoint genérico
    return Promise.reject(new Error('El endpoint getAll requiere id_comensal. Use getDietasByComensal(idComensal) en su lugar.'));
  },

  /**
   * Obtiene una asignación de dieta específica por ID
   * @param {number} id - ID de la asignación
   * @returns {Promise} - Respuesta de la API
   * 
   * NOTA: El backend de Python no tiene endpoint GET /dietas/{id} directo.
   * Se debe obtener primero las dietas del comensal y filtrar por ID.
   */
  getById: (id) => {
    // El backend de Python no tiene este endpoint directo
    return Promise.reject(new Error('Endpoint GET /dietas/{id} no está disponible en Python. Use getDietasByComensal y filtre por ID.'));
  },

  /**
   * Obtiene las dietas activas de un comensal específico
   * @param {number} idComensal - ID del comensal
   * @returns {Promise} - Respuesta de la API
   */
  getDietasByComensal: (idComensal) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.get(`/mantenimientos/comensales/${idComensal}/dietas`);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.get(`/comensales/${idComensal}/dietas`);
  },

  /**
   * Crea una nueva asignación de dieta
   * @param {Object} data - Datos de la asignación
   * @param {number} data.id_proyecto - ID del proyecto
   * @param {number} data.id_comensal - ID del comensal
   * @param {string} data.numero_documento_comedor - Número de documento de comedor
   * @param {number} data.id_tipo_dieta - ID del tipo de dieta
   * @param {string} data.fecha_inicio - Fecha de inicio (YYYY-MM-DD)
   * @param {string} data.fecha_fin - Fecha de fin (YYYY-MM-DD)
   * @param {string} data.observaciones - Observaciones opcionales
   * @param {boolean} data.activo - Estado activo (default: true)
   * @returns {Promise} - Respuesta de la API
   */
  create: (data) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.post('/mantenimientos/comensales/dietas', data);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.post('/comensales-dieta', data);
  },

  /**
   * Actualiza una asignación de dieta existente
   * @param {number} id - ID de la asignación
   * @param {Object} data - Datos actualizados de la asignación
   * @returns {Promise} - Respuesta de la API
   */
  update: (id, data) => {
    // Usar endpoint de Python (FastAPI)
    return pythonApiClient.put(`/mantenimientos/comensales/dietas/${id}`, data);
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.put(`/comensales-dieta/${id}`, data);
  },

  /**
   * Elimina una asignación de dieta
   * @param {number} id - ID de la asignación
   * @returns {Promise} - Respuesta de la API
   * 
   * NOTA: El backend de Python no tiene endpoint DELETE específico para dietas.
   * Se puede desactivar la dieta usando el endpoint PUT para actualizar activo=false
   */
  delete: (id) => {
    // El backend de Python no tiene DELETE directo, usar PUT para desactivar
    return pythonApiClient.put(`/mantenimientos/comensales/dietas/${id}`, { activo: false });
    
    // COMENTADO: Endpoint de PHP (Laravel) - rollback disponible si es necesario
    // return apiClient.delete(`/comensales-dieta/${id}`);
  },

  /**
   * Obtiene asignaciones de dieta vigentes para una fecha específica
   * @param {string} fecha - Fecha en formato YYYY-MM-DD
   * @param {Object} additionalFilters - Filtros adicionales opcionales
   * @returns {Promise} - Respuesta de la API
   */
  getDietasVigentes: (fecha, additionalFilters = {}) => {
    const filters = {
      fecha_vigente: fecha,
      activo: true,
      ...additionalFilters
    };
    return this.getAll(filters);
  }
};

export default comensalDietaApi;
